import axios from 'axios'
import store from '../index'
import { make } from 'vuex-pathify'
import { getQueryFromFilter, showError, showSuccess } from '@/util/helpers'
import { NFSE_PROVIDER_NACIONAL } from '@/enums/fiscal'

const state = {
  company: {
    addresses: [],
  },
  companies: {
    data: [],
    count: 0,
    limit: 50,
  },

  filter: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['name'],
    sortDesc: [false],
  },

  type: 'edit',
  dialogView: false,
  loading: false,
}

const mutations = make.mutations(state)

const actions = {
  view: ({ state }) => {
    state.dialogView = true
  },

  add: ({ state }) => {
    state.company = {
      addresses: [],
      active: true,
      emailProvider: 'custom',
      authType: 'basic',
      pixEnabled: false,
      pixKey: null,
      nfseProvider: NFSE_PROVIDER_NACIONAL,
      tpAmb: 2,
      nfeTpAmb: 2,
      nfseTpAmb: 2,
      nfseIssDeductionEnabled: false,
      nfseEffectiveIssAliquot: null,
      nfseIssWithholding: 2,
      nfseIsMei: false,
      nfseSimplesTaxAssessmentRegime: null,
      addServiceDescriptionToNfse: false,
      addTotalTaxToNfse: false,
      addPaymentDataToNfse: false,
      companyId: store.get('authStore@currentCompany').id,
    }

    state.type = 'add'
    state.dialogView = false
  },

  edit: ({ state, dispatch }, id) => {
    state.loading = true
    store.dispatch('companyStore/getById', id)
      .then(res => {
        state.company = res.data
        store.commit('authStore/setCompany', res.data)
        dispatch('validateCompany', res.data)
        state.type = 'edit'
        state.dialogView = false
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  validateCompany: (context, company) => {
    if (company.addresses?.length === 0) {
      return store.dispatch('app/showErrorCompany', 'Nenhum endereço da empresa encontrado')
    }

    if (!company.stateRegistration) {
      return store.dispatch('app/showErrorCompany', 'Inscrição estadual da empresa não cadastrada')
    }

    if (!company.logoSmall) {
      return store.dispatch('app/showErrorCompany', 'Logo da empresa não cadastrada')
    }

    return true
  },

  save: ({ state }, company) => {
    const id = company.id
    const method = id ? 'put' : 'post'

    state.loading = true
    const payload = { ...company }
    delete payload.logoSmallUrl
    delete payload.logoSmallPreview
    delete payload.hasEmailPassword
    delete payload.hasOauthClientSecret
    delete payload.hasOauthRefreshToken
    delete payload.hasPfxPassword
    delete payload.hasSimplissPassword
    delete payload.hasElotechPassword
    delete payload.hasMasterKey
    delete payload.daysRemaining
    delete payload.openBillingNotice
    return axios[method](`${process.env.VUE_APP_API_URL}/company/${id || ''}`, payload)
      .then((res) => {
        const { billingSync, billingSyncError, billingOwnerUpdate } = res.data || {}
        const currentCompany = store.get('authStore@currentCompany')
        const sanitizedCompany = { ...currentCompany, ...payload }
        const resolvedLogo = company.logoSmallPreview || company.logoSmallUrl || company.logoSmall
        if (resolvedLogo) {
          sanitizedCompany.logoSmallUrl = resolvedLogo
        }
        delete sanitizedCompany.logoSmallPreview
        if (sanitizedCompany.pfxPassword || currentCompany?.hasPfxPassword) {
          sanitizedCompany.hasPfxPassword = true
        }
        delete sanitizedCompany.pfxPassword
        if (sanitizedCompany.emailPassword || currentCompany?.hasEmailPassword) {
          sanitizedCompany.hasEmailPassword = true
        }
        delete sanitizedCompany.emailPassword
        if (sanitizedCompany.clientSecret || currentCompany?.hasOauthClientSecret) {
          sanitizedCompany.hasOauthClientSecret = true
        }
        delete sanitizedCompany.clientSecret
        if (sanitizedCompany.refreshToken || currentCompany?.hasOauthRefreshToken) {
          sanitizedCompany.hasOauthRefreshToken = true
        }
        delete sanitizedCompany.refreshToken
        if (sanitizedCompany.simplissPassword || currentCompany?.hasSimplissPassword) {
          sanitizedCompany.hasSimplissPassword = true
        }
        delete sanitizedCompany.simplissPassword
        if (sanitizedCompany.elotechPassword || currentCompany?.hasElotechPassword) {
          sanitizedCompany.hasElotechPassword = true
        }
        delete sanitizedCompany.elotechPassword

        if (company.id && company.id === store.get('authStore@currentCompany').id) {
          store.commit('authStore/setCompany', { ...sanitizedCompany })
        }

        if (billingOwnerUpdate?.id && billingOwnerUpdate.id === currentCompany?.id) {
          store.commit('authStore/setCompany', {
            ...currentCompany,
            ...billingOwnerUpdate,
          })
        }

        state.dialog = false
        store.dispatch('companyStore/get')
        if (billingSync?.status === 'FAILED' || billingSyncError) {
          showError(`Empresa salva, mas falhou ao atualizar a cobrança: ${billingSync?.reason || billingSyncError}`)
        } else if (billingSync?.status === 'SKIPPED') {
          showSuccess('Empresa salva. A cobrança foi recalculada sem integração externa.')
        } else {
          showSuccess('Empresa salva com sucesso!')
        }
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  loadCombos: async () => {
    store.dispatch('userStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })
  },

  get: async ({ state }, filter = state.filter) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/company?${getQueryFromFilter(filter)}`)
      .then(res => {
        state.companies = res.data

        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getById: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/company/${id}`).catch(showError),

  remove: ({ state }, id) => {
    state.loading = true
    return axios.delete(`${process.env.VUE_APP_API_URL}/company/${id}`)
      .then(_ => {
        store.dispatch('companyStore/get')
        showSuccess('Empresa excluída com sucesso!')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  uploadPfx: (context, form) => axios.post(`${process.env.VUE_APP_API_URL}/company-upload-pfx`, form).catch(showError),

  refreshToken: async ({ state }, { cnpj, days }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/company-refresh-token`, { cnpj, days })
      .then(_ => showSuccess())
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  saveFeedback: async ({ commit }, feedback) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/feedback`, {
      ...feedback,
      companyId: store.get('authStore@currentCompany').id,
      userId: store.get('authStore@currentUser').id,
    })
      .then(_ => {
        showSuccess('Obrigado pelo feedback')
        commit('app/dialogFeedback', false, { root: true })
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },
}

const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
